import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UniHeader } from "./_lmsShared";
import { Search, Award, Loader2, ShieldCheck } from "lucide-react";

/**
 * KISSED Alumni Directory (public view at /university/alumni + /community/alumni).
 *
 * 2026-07-06 · Privacy hotfix — this page now queries the Wave 1
 * `/api/university/alumni/verify` endpoint exclusively. That endpoint
 * respects the Wave 1 opt-in flag (`admins.public_directory_opt_in`)
 * and only surfaces alumni who have explicitly opted in.
 *
 * Legacy `/api/university/alumni/directory` (which queried a separate
 * collection and did NOT respect the Wave 1 opt-in) has been retired —
 * returns HTTP 410 with a migration pointer as of the same date.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university`;

const PROGRAM_LABEL = {
  level_1_scalp_scientist: "Certified Scalp Scientist (Level I)",
  level_2_associate_tri: "Associate Trichologist (Level II)",
  level_3_holistic_practitioner: "Holistic Trichology Practitioner (Level III)",
  herbal_foundations: "Herbal Medicine Foundations",
  herbal_practitioner: "Herbal Medicine Practitioner",
  dts: "Doctoral Trichology Scholar (DTS)",
  cct: "Clinically Certified Trichologist (CCT)",
  holistic_functional_wellness: "Holistic Health & Functional Wellness Practitioner",
};

function credentialLabel(slug) {
  return PROGRAM_LABEL[slug] || slug;
}

function completedYear(iso) {
  if (!iso) return "";
  try { return new Date(iso).getFullYear(); } catch { return ""; }
}

export default function UniversityAlumniDirectory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState(false);

  const load = async (query) => {
    const clean = (query ?? q).trim();
    if (clean.length < 2) {
      toast.error("Please enter at least 2 characters to search.");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const r = await axios.get(`${API}/alumni/verify`, { params: { q: clean } });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Could not load directory");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { /* no autoload — search is intent-driven */ }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Alumni Directory" back="/university" />
      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="uni-alumni-directory">
        <h1 className="font-display text-4xl text-slate-900">KISSED Alumni · Verify a Graduate</h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Confirm a practitioner&apos;s credentials before booking. Only alumni who have
          opted in to the public directory are listed here.
        </p>
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Opt-in verified · privacy respected
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); load(q); }}
          className="mt-6 flex gap-2"
          data-testid="uni-alumni-search"
        >
          <input
            className="flex-1 rounded-xl border border-slate-200 px-3 h-11 text-sm"
            placeholder="Search by name (min 2 characters)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="uni-alumni-search-input"
          />
          <button
            className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-11 px-5 inline-flex items-center gap-1"
            type="submit"
            disabled={loading}
            data-testid="uni-alumni-search-btn"
          >
            <Search className="w-4 h-4" /> Search
          </button>
        </form>

        <div className="mt-8">
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2" data-testid="uni-alumni-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          ) : !searched ? (
            <div
              className="rounded-xl border border-dashed border-slate-300 p-8 text-sm text-slate-500 text-center"
              data-testid="uni-alumni-empty"
            >
              Enter a name above to verify a graduate.
            </div>
          ) : items.length === 0 ? (
            <div
              className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center"
              data-testid="uni-alumni-no-results"
            >
              No alumni match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((a, idx) => (
                <article
                  key={`${a.name}-${idx}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5"
                  data-testid={`uni-alumni-card-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-900 font-display flex-shrink-0">
                      {(a.name || "A")[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-lg text-slate-900 leading-tight">
                        {a.name || "—"}
                      </div>
                      {a.clinic_name && (
                        <div className="text-xs text-slate-600 mt-0.5">{a.clinic_name}</div>
                      )}
                    </div>
                  </div>
                  {(a.credentials || []).length > 0 ? (
                    <ul className="mt-4 space-y-1.5 text-xs text-slate-700">
                      {a.credentials.map((c, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <Award className="w-3.5 h-3.5 text-purple-900 flex-shrink-0 mt-0.5" />
                          <span>
                            <span className="font-medium">{credentialLabel(c.program_slug)}</span>
                            {completedYear(c.completed_at) && (
                              <span className="text-slate-500"> · {completedYear(c.completed_at)}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-4 text-xs text-slate-400 italic">
                      No public credentials listed.
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
